import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Banknote, Bell, CreditCard, Crown, Printer, UserCog } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useIsDesktop } from '@/shared/hooks/useIsDesktop';
import { useSubscription } from '@/features/subscriptions/useSubscription';

interface Tab {
  id: string;
  path: string;
  label: string;
  icon: React.ElementType;
  /** Active when the URL is at this path or starts with it, but NOT when a
   *  more-specific sibling owns the deeper segment. */
  excludePaths?: string[];
  /** When true, the tab only renders inside the Electron desktop shell. */
  desktopOnly?: boolean;
  /** When true, the tab is hidden unless the user has an active subscription. */
  requiresActiveSubscription?: boolean;
}

const tabs: Tab[] = [
  { id: 'account', path: '/app/settings', label: 'account.nav.settings', icon: UserCog, excludePaths: ['/app/settings/payments', '/app/settings/printer'] },
  { id: 'subscription', path: '/app/billing', label: 'account.nav.subscription', icon: Crown },
  {
    id: 'payments',
    path: '/app/settings/payments',
    label: 'account.nav.payments',
    icon: CreditCard,
    excludePaths: ['/app/settings/payments/payouts'],
    requiresActiveSubscription: true,
  },
  {
    id: 'payouts',
    path: '/app/settings/payments/payouts',
    label: 'account.nav.payouts',
    icon: Banknote,
    requiresActiveSubscription: true,
  },
  {
    id: 'notifications',
    path: '/app/settings/notifications',
    label: 'account.nav.notifications',
    icon: Bell,
  },
  {
    id: 'printer',
    path: '/app/settings/printer',
    label: 'account.nav.printer',
    icon: Printer,
    desktopOnly: true,
  },
];

/**
 * Horizontal tab strip wrapping account-level routes (profile, billing,
 * payments, payouts). Provides consistent sub-navigation that pairs with the
 * persistent sidebar on the outside.
 */
export function AccountSectionLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const { isActive: hasActiveSubscription } = useSubscription();

  const visibleTabs = tabs.filter(
    (tab) =>
      (!tab.desktopOnly || isDesktop) &&
      (!tab.requiresActiveSubscription || hasActiveSubscription),
  );

  const navRef = useRef<HTMLElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  const isActive = (tab: Tab) => {
    const here = location.pathname;
    if (here !== tab.path && !here.startsWith(`${tab.path}/`)) return false;
    if (tab.excludePaths?.some((ex) => here === ex || here.startsWith(`${ex}/`))) {
      return false;
    }
    return true;
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <header className="border-b border-border bg-card/40 backdrop-blur-sm">
        <div className="px-4 lg:px-6 pt-5 pb-3">
          <h1 className="text-lg lg:text-xl font-semibold tracking-tight">
            {t('account.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t('account.subtitle')}</p>
        </div>
        <div className="relative">
          {/* Left fade */}
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 transition-opacity duration-150',
              'bg-gradient-to-r from-card/90 to-transparent',
              canScrollLeft ? 'opacity-100' : 'opacity-0',
            )}
          />
          {/* Right fade */}
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10 transition-opacity duration-150',
              'bg-gradient-to-l from-card/90 to-transparent',
              canScrollRight ? 'opacity-100' : 'opacity-0',
            )}
          />
          <nav ref={navRef} aria-label={t('account.title')} className="px-4 lg:px-6 overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1 min-w-max">
              {visibleTabs.map((tab) => (
                <NavLink
                  key={tab.id}
                  to={tab.path}
                  end={tab.id === 'account'}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2.5 text-sm font-medium whitespace-nowrap',
                    'border-b-2 -mb-px transition-colors',
                    isActive(tab)
                      ? 'border-accent text-accent'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
                  )}
                >
                  <tab.icon className="h-4 w-4" />
                  {t(tab.label)}
                </NavLink>
              ))}
            </div>
          </nav>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
